import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Save, Upload, Building2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import BlueprintMark from '@/components/BlueprintMark';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reads a File as a base64 data URI, same low-tech storage pattern every
// other image in the app already uses (no object storage / upload endpoint
// needed -- a logo is small enough to live directly on the user document).
const fileToDataUri = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    company_name: '', company_logo: '', company_address: '',
    company_phone: '', company_email: '', company_website: '', company_whatsapp: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        company_name: user.company_name || '',
        company_logo: user.company_logo || '',
        company_address: user.company_address || '',
        company_phone: user.company_phone || '',
        company_email: user.company_email || '',
        company_website: user.company_website || '',
        company_whatsapp: user.company_whatsapp || '',
      });
    }
  }, [user]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUri = await fileToDataUri(file);
      handleChange('company_logo', dataUri);
    } catch (error) {
      toast.error('Failed to read logo file');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/auth/company`, form, { withCredentials: true });
      await checkAuth();
      toast.success('Company profile saved');
    } catch (error) {
      toast.error('Failed to save company profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background blueprint-grid">
      <header className="h-16 border-b border-border flex items-center justify-between px-6 bg-card">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="settings-back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="font-heading font-semibold text-foreground text-lg">Company Settings</h1>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50" data-testid="settings-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
      </header>

      <div className="max-w-2xl mx-auto p-8">
        <p className="text-muted-foreground mb-8">
          This appears as the letterhead on every report you generate — your logo, address, and phone number, so reports read as coming from a real agency.
        </p>

        <div className="glass border border-border p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <h3 className="font-heading text-lg text-foreground">Company Identity</h3>
          </div>

          <div className="space-y-5">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 border border-border flex items-center justify-center bg-black/[0.03] overflow-hidden shrink-0">
                {form.company_logo ? (
                  <img src={form.company_logo} alt="Company logo" className="w-full h-full object-contain" />
                ) : (
                  <BlueprintMark className="w-8 h-8 text-muted-foreground/50" />
                )}
              </div>
              <div>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="company-logo-input" />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06]"
                  data-testid="company-logo-upload-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Logo
                </Button>
                <p className="text-xs text-muted-foreground mt-1.5">PNG or SVG with a transparent background works best</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground/80">Company Name</Label>
              <Input
                value={form.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="Blueprints"
                className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                data-testid="company-name-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-foreground/80">Phone</Label>
                <Input
                  value={form.company_phone}
                  onChange={(e) => handleChange('company_phone', e.target.value)}
                  placeholder="+1 (555) 010-1234"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="company-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground/80">Email</Label>
                <Input
                  value={form.company_email}
                  onChange={(e) => handleChange('company_email', e.target.value)}
                  placeholder="hello@blueprints.agency"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="company-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground/80">Address</Label>
              <Input
                value={form.company_address}
                onChange={(e) => handleChange('company_address', e.target.value)}
                placeholder="123 Design St, Sydney NSW"
                className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                data-testid="company-address-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-foreground/80">Website</Label>
                <Input
                  value={form.company_website}
                  onChange={(e) => handleChange('company_website', e.target.value)}
                  placeholder="blueprints.agency"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="company-website-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground/80">WhatsApp</Label>
                <Input
                  value={form.company_whatsapp}
                  onChange={(e) => handleChange('company_whatsapp', e.target.value)}
                  placeholder="+1 (555) 010-1234"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="company-whatsapp-input"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
